<template>
  <div ref="actionsRef" class="modal-repositories-actions">
    <UIButton
      v-tooltip="importTooltip"
      data-refid="repositoryImport"
      label="Import"
      :disabled
      :size
      :leading="ArrowDownTrayIcon"
      variant="secondary"
      hide-label-to-sm
      @click="handleImport"
    />
    <UIButton
      v-tooltip="createTooltip"
      data-refid="repositoryCreate"
      :disabled
      :size
      :leading="PlusIcon"
      label="New"
      @click="handleCreate"
    />
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { computed, markRaw, ref, useTemplateRef } from 'vue'

import { ButtonSize } from '@types'

import { useModalsStore } from '@/store/modals'

import RepositoryCreate from '@/modules/modals/Repositories/Data/RepositoryCreate.vue'
import RepositoryImport from '@/modules/modals/Repositories/Data/RepositoryImport.vue'

import { ArrowDownTrayIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'
import { onClickOutside } from '@vueuse/core'

type Props = {
  disabled?: boolean
  unavailable?: boolean
  size?: ButtonSize
}

type Emits = {
  click: []
  close: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const modalsStore = useModalsStore()

const actionsRef = useTemplateRef('actionsRef')

const isImportTooltipShown = ref(false)
const isCreateTooltipShown = ref(false)

const handleImport = () => {
  if (props.unavailable) {
    isCreateTooltipShown.value = false
    isImportTooltipShown.value = true
    return
  }
  emit('click')
  const modalInstance = modalsStore.init(
    'repository-import',
    markRaw(RepositoryImport),
  )
  modalInstance?.open(modalsStore.getZIndex())
}

const importTooltip = computed(() => ({
  content: 'Upgrade your plan to use this feature',
  triggers: ['manual'],
  placement: 'top',
  shown: isImportTooltipShown.value,
}))

const createTooltip = computed(() => ({
  content: 'Upgrade your plan to use this feature',
  triggers: ['manual'],
  placement: 'top',
  shown: isCreateTooltipShown.value,
}))

const handleCreate = () => {
  if (props.unavailable) {
    isImportTooltipShown.value = false
    isCreateTooltipShown.value = true
    return
  }
  emit('click')
  const modalInstance = modalsStore.init(
    'repository-create',
    markRaw(RepositoryCreate),
  )
  modalInstance?.open(modalsStore.getZIndex(), {
    callback: () => {
      emit('close')
    },
  })
}

onClickOutside(actionsRef, () => {
  isCreateTooltipShown.value = false
  isImportTooltipShown.value = false
})
</script>

<style scoped>
.modal-repositories-actions {
  @apply flex items-center justify-between;
  @apply gap-2;

  :deep() .ui-button {
    @apply whitespace-nowrap;
  }
}
</style>
